import React, { useMemo, useState } from "react";
import { useIntl, FormattedMessage } from "react-intl";
import Select from "react-select/creatable";
import { unflatten } from "flat";
import Nav from "../Nav";
import Footer from "../Footer";
import Section from "../Section";
import Header from "../Header";
import {
  Form,
  Row,
  Label,
  Col,
  Button,
  Textarea,
  Input,
  FileInput,
  CheckboxWrapper,
  Service,
  Services,
} from "./styled";
import Container from "../Container";
import Anchor from "../Anchor";
import { useTheme } from "styled-components";
import { transparentize } from "polished";

export const Body: React.FC = () => {
  const { messages, formatMessage } = useIntl();
  const content = unflatten(messages);
  const scTheme = useTheme();
  const [services, setServices] = useState<string[]>([]);

  const languagesOptions = useMemo(
    () =>
      content.form.languages.map((l) => ({ value: l.toLowerCase(), label: l })),
    [content]
  );

  const toolsOptions = useMemo(
    () =>
      content.form.tools.options.map((o) => ({
        value: o.toLowerCase(),
        label: o,
      })),
    [content]
  );

  const customSelectTheme = (theme) => ({
    ...theme,
    borderRadius: 0,
    colors: {
      ...theme.colors,
      primary: scTheme.palette.primary.default,
      primary25: transparentize(0.75, scTheme.palette.primary.default),
    },
  });

  return (
    <Section>
      <Header
        background={messages["header.background"] as string}
        height={420}
        objectPosition="center 30%"
        title={formatMessage({ id: "header.title" })}
        circlePosition="48%"
      />
      <Container dTop={8} dBottom={8} mTop={4} mBottom={4}>
        <Form
          data-netlify="true"
          name="apply"
          method="post"
          data-netlify-honeypot="bot-field"
          encType="multipart/form-data"
        >
          <input type="hidden" name="form-name" value="apply" />
          <Row marginBottom={4}>
            <Col>
              <Label>
                <FormattedMessage id="form.details.label" />
              </Label>
              <Row marginBottom={2}>
                <Col width={50} marginRight={1}>
                  <Input
                    type="text"
                    name="firstname"
                    placeholder={formatMessage({
                      id: "form.details.inputs.firstname",
                    })}
                    required
                  />
                </Col>
                <Col width={50} marginLeft={1}>
                  <Input
                    type="text"
                    name="lastname"
                    placeholder={formatMessage({
                      id: "form.details.inputs.lastname",
                    })}
                    required
                  />
                </Col>
              </Row>
              <Input
                type="email"
                name="email"
                placeholder={formatMessage({
                  id: "form.details.inputs.email",
                })}
                required
              />
            </Col>
          </Row>
          <Row marginBottom={4}>
            <Col width={100 / 3} marginRight={1}>
              <Label>
                <FormattedMessage id="form.native.label" />
              </Label>
              <Select
                options={languagesOptions}
                isSearchable
                isClearable
                name="native_language"
                placeholder={formatMessage({
                  id: "form.native.placeholder",
                })}
                theme={customSelectTheme}
                required
              />
            </Col>
            <Col width={100 / 3} marginRight={1} marginLeft={1}>
              <Label>
                <FormattedMessage id="form.source.label" />
              </Label>
              <Select
                options={languagesOptions}
                isMulti
                isSearchable
                isClearable
                name="source_languages[]"
                placeholder={formatMessage({
                  id: "form.source.placeholder",
                })}
                theme={customSelectTheme}
                required
              />
            </Col>
            <Col width={100 / 3} marginLeft={1}>
              <Label>
                <FormattedMessage id="form.tools.label" />
              </Label>
              <Select
                options={toolsOptions}
                isMulti
                isSearchable
                isClearable
                name="cat_tools[]"
                placeholder={formatMessage({
                  id: "form.tools.placeholder",
                })}
                theme={customSelectTheme}
                required
              />
            </Col>
          </Row>
          <Row marginBottom={4}>
            <Col>
              <Label>
                <FormattedMessage id="form.services.label" />
              </Label>
              <Services>
                {content.form.services.options.map((o, i) => (
                  <Service key={o.value}>
                    <CheckboxWrapper>
                      <Input
                        id={`checkbox.${i}`}
                        name="services[]"
                        type="checkbox"
                        value={o.value}
                        checked={services.includes(o.value)}
                        onChange={() => {
                          if (services.includes(o.value)) {
                            setServices(services.filter((s) => s !== o.value));
                            return;
                          }

                          setServices([...services, o.value]);
                        }}
                      />
                      <label htmlFor={`checkbox.${i}`}>
                        <FormattedMessage
                          id={`form.services.options.${i}.label`}
                        />
                      </label>
                    </CheckboxWrapper>
                    <input
                      type="number"
                      min={0}
                      name={`${o.value}_fee`}
                      disabled={services.includes(o.value) === false}
                      defaultValue={0}
                      step={0.01}
                    />
                  </Service>
                ))}
              </Services>
            </Col>
          </Row>
          <Row marginBottom={4}>
            <Col width={50} marginRight={1}>
              <Label>
                <FormattedMessage id="form.resume.label" />
              </Label>
              <FileInput
                type="file"
                name="resume"
                placeholder={formatMessage({
                  id: "form.resume.placeholder",
                })}
                accept=".pdf,.doc,.docx,.rtf,.odt,.ppt,.pptx"
                required
              />
            </Col>
            <Col width={50} marginLeft={1}>
              <Label>
                <FormattedMessage id="form.letter.label" />
              </Label>
              <FileInput
                type="file"
                name="cover_letter"
                placeholder={formatMessage({
                  id: "form.letter.placeholder",
                })}
                accept=".pdf,.doc,.docx,.rtf,.odt,.ppt,.pptx"
              />
            </Col>
          </Row>
          <Row marginBottom={4}>
            <Col>
              <Label>
                <FormattedMessage id="form.comments.label" />
              </Label>
              <Textarea
                name="comments"
                placeholder={formatMessage({
                  id: "form.comments.placeholder",
                })}
              />
            </Col>
          </Row>
          <Button type="submit">
            <FormattedMessage id="form.submit" />
          </Button>
        </Form>
      </Container>
    </Section>
  );
};

const Apply: React.FC = () => {
  return (
    <>
      <Nav />
      <Anchor id="apply" first />
      <Body />
      <Footer />
    </>
  );
};

export default Apply;
